import 'components/inline-edit/ibm-products-config';
import { SidePanel } from '@carbon/ibm-products';
import { InlineLoading, InlineNotification } from '@carbon/react';
import classNames from 'classnames';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useState, type ReactNode } from 'react';

import { SuccessToast } from 'components/success-toast/success-toast';
import { format0 } from 'utils/number';

import {
  comparisonTableSelectionAtom,
  confirmPanelStateAtom,
  customLossWarningAtom,
  type ConfirmTractEntry,
} from '../atoms';
import { useConfirmationMutations } from '../hooks';
import type {
  BulkFailureEntry,
  SlotSubtract,
  StreamCode,
  TractDraft,
} from '../types';
import {
  buildTractEntry,
  countRowsWithPriorCustomConfirmation,
  EVIDENCE_SOURCE_LABELS,
  getMutationErrorMessage,
  hasPriorCustomConfirmation,
  isEligibleForBulkAm,
  isTractAmSelectable,
  rowKey,
  rowHasPriorCustomConfirmation,
  tractKey,
} from '../utils';
import { useWorkspaceConfirmation } from '../workspace-confirmation-context';
import { BulkContent } from './bulk-content';
import { SingleSlotContent } from './single-slot-content';
import styles from './confirm-interest-panel.module.scss';

/**
 * Orchestrates the Confirm Interest side panel. Reads the open-state atom,
 * dispatches to the per-tract single-slot body or the bulk body, and owns the
 * footer actions plus the success / error notifications. The per-tract form
 * lives in `WorkspaceConfirmationProvider`; tract sections submit through
 * `submitTracts` and this panel tracks the session's certified streams so
 * sections flip to their certified summary without waiting on a row refetch.
 *
 * @return {JSX.Element} The rendered side panel (hidden when closed).
 */
export const ConfirmInterestPanel = (): JSX.Element => {
  const [panelState, setPanelState] = useAtom(confirmPanelStateAtom);
  const setTableSelection = useSetAtom(comparisonTableSelectionAtom);
  const setCustomLossWarning = useSetAtom(customLossWarningAtom);
  const { activeRow, activeRowStale, submitTracts, form } =
    useWorkspaceConfirmation();
  const { submitBulkConfirm } = useConfirmationMutations();

  // Transient per-open outcome state. Cleared on each new open / close / row
  // change via the sessionKey effect below.
  const [submitting, setSubmitting] = useState(false);
  const [bulkFailures, setBulkFailures] = useState<BulkFailureEntry[] | null>(
    null
  );
  const [panelError, setPanelError] = useState<string | null>(null);
  // Optional supporting note for a bulk certify — applied to every selected
  // slot. AM never requires it, so it never gates the Certify action.
  const [bulkNote, setBulkNote] = useState('');
  // Streams certified per tract THIS session — drives the sections' certified
  // summaries between submit and table refetch.
  const [sessionCertified, setSessionCertified] = useState<
    Record<string, StreamCode[]>
  >({});
  // Decoupled from the panel session so the toast survives the panel closing.
  const [successToastMessage, setSuccessToastMessage] = useState<string | null>(
    null
  );

  const sessionKey =
    panelState === null
      ? null
      : panelState.mode === 'single'
        ? `single-${rowKey(panelState.rows[0])}`
        : `bulk-${panelState.rows.length}`;

  useEffect(() => {
    setSubmitting(false);
    setBulkFailures(null);
    setPanelError(null);
    setSessionCertified({});
    setBulkNote('');
  }, [sessionKey]);

  const handleClose = (): void => {
    setPanelState(null);
    setTableSelection({ selectAll: false, toggledNodes: [] });
  };

  const recordSessionCertified = (entries: ConfirmTractEntry[]): void => {
    setSessionCertified((prev) => {
      const next = { ...prev };
      for (const entry of entries) {
        const key = tractKey(entry.subtract_id, entry.unit_allocation_tract_id);
        next[key] = [...new Set([...(next[key] ?? []), ...entry.streams])];
      }
      return next;
    });
  };

  const submitTract = async (
    subtract: SlotSubtract | null,
    draft: TractDraft
  ): Promise<void> => {
    if (draft.source === null) return;
    // The card's own draft carries its per-card supporting note.
    const entry = buildTractEntry(subtract, draft);
    setPanelError(null);
    setSubmitting(true);
    try {
      await submitTracts([entry]);
      recordSessionCertified([entry]);
      // Name the certified tract in the toast, matching the card subtitle
      // pattern (Tract · Subtract · UAT). Falls back to the source-only message
      // for the no-subtract case.
      const identity = subtract
        ? [
            subtract.tractName ?? subtract.subtractName,
            subtract.tractName ? subtract.subtractName : null,
            subtract.uatName ? `UAT: ${subtract.uatName}` : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : null;
      setSuccessToastMessage(
        identity
          ? `Certified ${identity} — ${EVIDENCE_SOURCE_LABELS[entry.source]}`
          : `Certified: ${EVIDENCE_SOURCE_LABELS[entry.source]}`
      );
    } catch (error) {
      setPanelError(getMutationErrorMessage(error, "Couldn't certify tract."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmTract = async (
    subtract: SlotSubtract | null,
    draft: TractDraft
  ): Promise<void> => {
    if (draft.source === null) return;
    // Only switching a previously Custom (user_entered) tract to Allocation
    // Manager overwrites entered values — warn first, deferring the submit.
    if (
      draft.source === 'allocation_manager' &&
      activeRow &&
      hasPriorCustomConfirmation(activeRow, subtract)
    ) {
      setCustomLossWarning({
        count: 1,
        onConfirm: () => {
          void submitTract(subtract, draft);
        },
      });
      return;
    }
    await submitTract(subtract, draft);
  };

  // "Set AM as Evidence Source for All" — one atomic submit of an AM entry per
  // AM-eligible tract (all of the slot's streams). GIS-only tracts are skipped.
  const handleConfirmAllAm = async (): Promise<void> => {
    if (panelState?.mode !== 'single' || !activeRow) return;
    const tracts: Array<SlotSubtract | null> =
      activeRow.subtracts.length > 0 ? activeRow.subtracts : [null];
    const entries: ConfirmTractEntry[] = tracts
      .filter((subtract) => isTractAmSelectable(activeRow, subtract))
      .map((subtract) => {
        // Carry the supporting note the user typed in this tract's card (if
        // any). AM doesn't require a note, but the "Set AM for all" shortcut
        // must not silently drop one the user entered. Each (subtract, UAT)
        // segment is its own card / draft (ONCALL-668), so key on both.
        const draft =
          form.store.state.values.tracts[
            tractKey(subtract?.subtractId ?? null, subtract?.uatId ?? null)
          ];
        return {
          subtract_id: subtract?.subtractId ?? null,
          unit_allocation_tract_id: subtract?.uatId ?? null,
          source: 'allocation_manager',
          note: (draft?.note ?? '').trim() || null,
          streams: activeRow.streams,
        };
      });
    if (entries.length === 0) return;
    setPanelError(null);
    setSubmitting(true);
    try {
      await submitTracts(entries);
      recordSessionCertified(entries);
      setSuccessToastMessage('Certified all tracts to Allocation Manager');
    } catch (error) {
      setPanelError(
        getMutationErrorMessage(
          error,
          "Couldn't certify to Allocation Manager."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitBulk = async (): Promise<void> => {
    if (panelState?.mode !== 'bulk') return;
    setPanelError(null);
    setBulkFailures(null);
    setSubmitting(true);
    try {
      const result = await submitBulkConfirm(panelState.rows, bulkNote);
      if (result.failed.length > 0) {
        setBulkFailures(result.failed);
      } else {
        setSuccessToastMessage(
          `${format0(result.confirmedCount)} slots certified`
        );
        handleClose();
      }
    } catch (error) {
      setPanelError(getMutationErrorMessage(error, "Couldn't certify slots."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkConfirm = async (): Promise<void> => {
    if (panelState?.mode !== 'bulk') return;
    // Bulk always certifies to Allocation Manager — warn if any selected row
    // currently carries a Custom (user_entered) confirmation that would be
    // overwritten, deferring the submit.
    const customCount = countRowsWithPriorCustomConfirmation(panelState.rows);
    if (panelState.rows.some(rowHasPriorCustomConfirmation)) {
      setCustomLossWarning({
        count: customCount,
        onConfirm: () => {
          void submitBulk();
        },
      });
      return;
    }
    await submitBulk();
  };

  let title = '';
  let subtitle: ReactNode = '';
  if (panelState?.mode === 'single' && activeRow) {
    title = `Select Evidence Source — ${activeRow.slotId}`;
    subtitle = (
      <span
        className={classNames(styles.subtitleLine, styles.subtitleWellName)}
      >
        {activeRow.wellName}
      </span>
    );
  } else if (panelState?.mode === 'bulk') {
    title = `Certify ${format0(
      panelState.rows.length
    )} slots to Allocation Manager`;
    subtitle =
      'Bulk certifies each selected slot to its own Allocation Manager NRI/WI.';
  }

  let actions: {
    kind: 'primary' | 'secondary';
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }[] = [];
  if (panelState?.mode === 'single' && activeRow) {
    actions = [
      {
        kind: 'secondary',
        label: 'Close',
        onClick: handleClose,
        disabled: submitting,
      },
    ];
  } else if (panelState?.mode === 'bulk') {
    const eligibleCount = panelState.rows.filter(isEligibleForBulkAm).length;
    const total = panelState.rows.length;
    actions = [
      {
        kind: 'secondary',
        label: 'Cancel',
        onClick: handleClose,
        disabled: submitting,
      },
      {
        kind: 'primary',
        label:
          eligibleCount === total
            ? `Certify ${format0(total)} slots`
            : `Certify ${format0(eligibleCount)} of ${format0(total)} slots`,
        onClick: handleBulkConfirm,
        disabled: submitting || eligibleCount === 0,
      },
    ];
  }

  const allIneligible =
    panelState?.mode === 'bulk' &&
    bulkFailures === null &&
    !panelState.rows.some(isEligibleForBulkAm);

  return (
    <>
      <SidePanel
        open={panelState !== null}
        onRequestClose={handleClose}
        title={title}
        subtitle={subtitle}
        placement="right"
        size="md"
        animateTitle={false}
        slideIn
        selectorPageContent="#ic-workspace-main"
        actions={actions}
      >
        {/* `data-portal-container` keeps the AM Show Calculation / Show NRI
            Matrix popups (BaseCard) inside the SidePanel's stacking context so
            they render above the panel instead of behind it on document.body.
            Mirrors the wizard modal convention. */}
        <div className={styles.body} data-portal-container>
          {panelError !== null && (
            <InlineNotification
              className={styles.notification}
              kind="error"
              lowContrast
              hideCloseButton
              title={panelError}
            />
          )}

          {/* INS-11586: a confirmation event landed but no successful refetch
              has settled since, so the row below predates the user's edit —
              its review state may already be gone. Say so and lock the
              controls rather than let them act on it. */}
          {panelState?.mode === 'single' && activeRowStale && (
            <InlineNotification
              className={styles.notification}
              kind="warning"
              lowContrast
              hideCloseButton
              title="This slot is being refreshed"
              subtitle="Your change was saved. Editing is locked until the updated values load, so you don't act on out-of-date review state."
            />
          )}

          {allIneligible && (
            <InlineNotification
              className={styles.notification}
              kind="warning"
              lowContrast
              hideCloseButton
              title="No selected slots are eligible for bulk AM confirm."
            />
          )}

          {panelState?.mode === 'single' && activeRow && (
            <SingleSlotContent
              row={activeRow}
              sessionCertified={sessionCertified}
              onConfirmTract={handleConfirmTract}
              // A stale row locks the same controls a submit-in-flight does:
              // in both cases the values on screen aren't safe to act on.
              submitting={submitting || activeRowStale}
              onSetAmForAll={handleConfirmAllAm}
            />
          )}
          {panelState?.mode === 'bulk' && (
            <BulkContent
              rows={panelState.rows}
              failures={bulkFailures}
              note={bulkNote}
              onNoteChange={setBulkNote}
              submitting={submitting}
            />
          )}

          {submitting && (
            <InlineLoading
              className={styles.notification}
              description="Certifying…"
            />
          )}
        </div>
      </SidePanel>

      {successToastMessage !== null && (
        <SuccessToast
          message={successToastMessage}
          onClose={() => {
            setSuccessToastMessage(null);
            return false;
          }}
        />
      )}
    </>
  );
};
